<template>
  <div v-if="showPaymentInfo()">
    <button type="button" class="inline-btn-link" @click="changePaymentMethod()">
      <svg-icon-component icon="edit" class="icon-sm" />
      {{ paymentInfo() }}
    </button>
  </div>
  <div v-else-if="showPaymentInfoForNonInstalledDevice()">
    {{ paymentInfo() }}
  </div>
  <div v-else-if="showAddPaymentMethod()">
    <button type="button" class="inline-btn-link" @click="addPaymentMethod()">
      {{ t("SubscriptionInformationComponent.addNewPaymentMethod") }}
    </button>
  </div>
  <div v-else>{{ t("SubscriptionInformationComponent.notApplicable") }}</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import { ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { KeyCodeSeller } from "@/globalEnums";
import { SafeDataPurgeDays } from "@/define";
import { subscriptionPageState } from "./SubscriptionsEnums";
import { isWebrootProduct } from "@/common/subscriptionHelper";
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";

const componentName = "PaymentMethodColumnComponent";

export default defineComponent({
  name: componentName,
  components: {
    SvgIconComponent,
  },
  props: {
    sub: {
      type: Object as PropType<ISubscription>,
      required: true,
    },
  },
  emits: ["set-current-subscription", "subscription-page-state-change"],

  setup(props, context) {
    logEvent("created", componentName);

    function changePaymentMethod() {
      logEvent("changePaymentMethod");

      if (props.sub.keyCodeSellerType === KeyCodeSeller.Safe) {
        context.emit("set-current-subscription", props.sub);
        context.emit("subscription-page-state-change", subscriptionPageState.paymentMethodDetails);
      }
    }

    function showAddPaymentMethod(): boolean {
      if (props.sub && props.sub.isActiveUntilCancelled && !props.sub.isEvergreenMonthly) {
        return false;
      }

      return (
        props.sub.isEvergreenMonthly &&
        !props.sub.isCancelledEvergreenSubscription &&
        props.sub.expirationDays > 0 &&
        !props.sub.paymentInfo?.cardType &&
        !props.sub.isSafeLite
      );
    }

    function addPaymentMethod() {
      context.emit("subscription-page-state-change", subscriptionPageState.addPaymentMethod);
    }

    function showPaymentInfo(): boolean {
      if (!props.sub) {
        return false;
      } else if (props.sub.isActiveUntilCancelled && !props.sub.isEvergreenMonthly) {
        return false;
      } else {
        return (
          !!props.sub.paymentInfo?.cardType &&
          (!props.sub.resellerId || props.sub.isPartnerReferral) &&
          !props.sub.isTrial &&
          ((props.sub.isAutoRenewEnabled && !props.sub.isEvergreenMonthly) ||
            (props.sub.isEvergreenMonthly && !props.sub.isCancelledEvergreenSubscription)) &&
          props.sub.expirationDays > 0 &&
          !props.sub.computer?.dataHasBeenPurged &&
          props.sub.expirationDays > SafeDataPurgeDays.Direct &&
          props.sub.keyCodeSellerType !== KeyCodeSeller.BestBuy &&
          props.sub.keyCodeSellerType !== KeyCodeSeller.Amazon
        );
      }
    }

    function showPaymentInfoForNonInstalledDevice(): boolean {
      if (props.sub && props.sub.isActiveUntilCancelled && !props.sub.isEvergreenMonthly) {
        return false;
      }

      return (
        !!props.sub.paymentInfo?.cardType &&
        !props.sub.resellerId &&
        !props.sub.isTrial &&
        (!props.sub.computer || props.sub.computer.id === 0) &&
        props.sub.expirationDays > 0 &&
        props.sub.keyCodeSellerType === KeyCodeSeller.Safe &&
        !isWebrootProduct(props.sub) &&
        !props.sub.isSafeLite
      );
    }

    function paymentInfo() {
      if (props.sub.paymentInfo?.cardType.toLowerCase() === "paypal") {
        return props.sub.paymentInfo?.cardType;
      }

      return `${props.sub.paymentInfo?.cardType} *${getLastFourOfCC(
        props.sub.paymentInfo?.cardLastFourDigits as string
      )}`;
    }

    function getLastFourOfCC(cardNumber: string): string {
      return cardNumber.substring(cardNumber.length - 4, cardNumber.length);
    }

    //Expose to template
    return {
      t,
      showPaymentInfo,
      showPaymentInfoForNonInstalledDevice,
      changePaymentMethod,
      paymentInfo,
      showAddPaymentMethod,
      addPaymentMethod,
    };
  },
});
</script>
